// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * ADR-079 — how a client decides what an Amy turn is doing, from the server's
 * durable status, never from a socket's mood. Twin module: frontend/src/lib
 * and mobile/src/lib carry the byte-identical file (pinned by both test suites).
 *
 * The founder's report (2026-09-16): "If I ask Amy a question and move
 * between apps, she stops reasoning." The stream a turn rides on dies with
 * the app; the turn itself now finishes server-side (turn thread + status
 * record). This module is the ONE rule both clients use to turn that record
 * into a screen state:
 *
 *   idle → live → (stream loss) reconnecting → stillWorking | answered | failed | abandoned
 *
 * It replaces ADR-058's "nothing came back ⇒ resend" rule: a transparent
 * retry is allowed ONLY when the server has never seen the turn AND nothing
 * came back AND it is the first attempt. Any status the server knows is
 * resumed, never resent — a turn the server may still be running must not
 * be run twice. Pure: no storage, no React, no clocks except the `now`
 * values callers pass in.
 */
import { emptyAttemptProgress, type AttemptProgress } from "./amyInterruption"

export type TurnStatus = "running" | "completed" | "failed" | "abandoned"
/** What `GET /v1/amy/turns/{id}` told us — or that it could not be asked / knew nothing. */
export type ServerTurnStatus = TurnStatus | "unregistered" | "unreachable"

/** The on-device record of a turn in flight — enough to ask the server about it after a restart; never the full prompt. */
export interface PendingTurnRecord {
  v: 1
  turnId: string
  conversationId: string
  runtimeSessionId: string
  /** Client ISO at send; overwritten by the server's startedAt after the first successful status read. */
  startedAt: string
  /** The served deadline, once known. */
  expiresAt: string | null
  /** ≤ 120 chars, device-only — quoted back when the message never reached Amy. */
  promptPreview: string
}

export type FailureReason = "runtime" | "interrupted" | "unregistered" | "noAnswer" | "unreachable"

export type TurnPhase =
  | { kind: "idle" }
  | { kind: "live"; turnId: string; startedAt: number }
  | { kind: "reconnecting"; turnId: string; startedAt: number; attempt: 1 | 2 }
  | {
      kind: "stillWorking"
      turnId: string
      startedAt: number
      expiresAt: number | null
      polls: number
    }
  | { kind: "answered"; turnId: string; answeredWhileAway: boolean; newSince: number | null }
  | { kind: "failed"; turnId: string; reason: FailureReason }
  | { kind: "abandoned"; turnId: string }

export type TurnEvent =
  | { type: "send"; turnId: string; now: number }
  | { type: "turn_started"; turnId: string }
  | {
      type: "turn_completed"
      turnId: string
      answerRecorded: boolean
      screenFocused: boolean
      now: number
    }
  | { type: "turn_failed" | "turn_abandoned" | "turn_duplicate"; turnId: string }
  /** invoke() settled with no terminal lifecycle event — a legacy runtime; the stream itself was the answer. */
  | { type: "stream_end"; turnId: string }
  | { type: "stream_error"; turnId: string; attempt: 1 | 2 }
  /** The server's answer to GET /v1/amy/turns/{id} (or that it could not be asked). `attempt`/`progress` matter only for unregistered/unreachable. */
  | {
      type: "status"
      turnId: string
      status: ServerTurnStatus
      startedAt: string | null
      expiresAt: string | null
      answerRecorded: boolean
      screenFocused: boolean
      now: number
      attempt?: 1 | 2
      progress?: AttemptProgress
    }
  | { type: "poll_tick"; now: number }
  | { type: "reset" }

export const POLL_INTERVAL_MS = 2_000
/** First-GET fallback for a record without a served deadline: cap 300 s + grace 90 s (infra-cdk/lib/amy-turn-limits.ts). */
export const DEFAULT_TURN_TTL_MS = 390_000
/** A pending record older than this is dropped without a status read — history on mount already shows what happened. */
export const RECORD_MAX_AGE_MS = 24 * 60 * 60 * 1000
/** The client's own safety net past the served deadline: the server finalizes orphans on read; this only covers an unreachable server. */
const DEADLINE_SLACK_MS = 30_000

export type StreamLossDecision =
  { action: "resume" } | { action: "retry" } | { action: "interrupt" }

/**
 * ADR-058's condition, now gated on the server not knowing the turn: resend
 * only when nothing came back on the FIRST attempt AND the server has no row
 * for the id. Anything the server knows is resumed. `unreachable` is an
 * interruption, never a licence to resend.
 */
export function decideAfterStreamLoss(f: {
  attempt: 1 | 2
  progress: AttemptProgress
  serverStatus: ServerTurnStatus
}): StreamLossDecision {
  if (
    f.serverStatus === "running" ||
    f.serverStatus === "completed" ||
    f.serverStatus === "failed" ||
    f.serverStatus === "abandoned"
  ) {
    return { action: "resume" }
  }
  if (
    f.serverStatus === "unregistered" &&
    f.attempt === 1 &&
    !f.progress.receivedText &&
    !f.progress.sawToolActivity
  ) {
    return { action: "retry" }
  }
  return { action: "interrupt" }
}

/** Messages at or after this server timestamp are "the answer that arrived while you were away" (5 s of clock slack between the API and Memory). */
export function newAnswerMarker(turnStartedAt: string): number {
  return Date.parse(turnStartedAt) - 5_000
}

export function isAnsweredWhileAway(
  m: { role: string; at: string },
  newSince: number | null
): boolean {
  if (newSince === null || m.role !== "assistant") return false
  const at = Date.parse(m.at)
  return Number.isFinite(at) && at >= newSince
}

/** The ONE mapping from a served record (GET /v1/amy/turns/{id}) to the reducer's status event. */
export function statusEventFromTurn(
  turn: {
    id: string
    status: TurnStatus
    startedAt: string
    expiresAt: string | null
    answerRecorded: boolean
  },
  f: { screenFocused: boolean; now: number; attempt?: 1 | 2; progress?: AttemptProgress }
): Extract<TurnEvent, { type: "status" }> {
  return {
    type: "status",
    turnId: turn.id,
    status: turn.status,
    startedAt: turn.startedAt,
    expiresAt: turn.expiresAt,
    answerRecorded: turn.answerRecorded,
    screenFocused: f.screenFocused,
    now: f.now,
    ...(f.attempt ? { attempt: f.attempt } : {}),
    ...(f.progress ? { progress: f.progress } : {}),
  }
}

/** The status event for a read that found no row (404 → unregistered) or could not be made (unreachable). */
export function statusEventForMissing(
  turnId: string,
  status: "unregistered" | "unreachable",
  f: { screenFocused: boolean; now: number; attempt: 1 | 2; progress: AttemptProgress }
): Extract<TurnEvent, { type: "status" }> {
  return {
    type: "status",
    turnId,
    status,
    startedAt: null,
    expiresAt: null,
    answerRecorded: false,
    screenFocused: f.screenFocused,
    now: f.now,
    attempt: f.attempt,
    progress: f.progress,
  }
}

function deadlineOf(startedAt: number, expiresAt: number | null): number {
  return (expiresAt ?? startedAt + DEFAULT_TURN_TTL_MS) + DEADLINE_SLACK_MS
}

function parseIso(value: string | null): number | null {
  if (value === null) return null
  const t = Date.parse(value)
  return Number.isFinite(t) ? t : null
}

function failureAfterInterrupt(
  serverStatus: ServerTurnStatus,
  progress: AttemptProgress
): FailureReason {
  if (serverStatus === "unreachable") return "unreachable"
  return progress.receivedText || progress.sawToolActivity ? "interrupted" : "unregistered"
}

/** Total over every (phase, event): an event for another turn, or one that means nothing in this phase, changes nothing. */
export function reduceTurn(phase: TurnPhase, event: TurnEvent): TurnPhase {
  if (event.type === "reset") return { kind: "idle" }
  if (event.type === "send") return { kind: "live", turnId: event.turnId, startedAt: event.now }
  if (event.type === "poll_tick") {
    if (phase.kind === "stillWorking" && event.now > deadlineOf(phase.startedAt, phase.expiresAt))
      return { kind: "abandoned", turnId: phase.turnId }
    return phase
  }
  if (phase.kind === "idle") return phase
  if (event.turnId !== phase.turnId) return phase

  switch (phase.kind) {
    case "live":
      switch (event.type) {
        case "turn_started":
          return phase
        case "turn_completed":
          return event.answerRecorded
            ? {
                kind: "answered",
                turnId: phase.turnId,
                answeredWhileAway: !event.screenFocused,
                newSince: null,
              }
            : { kind: "failed", turnId: phase.turnId, reason: "noAnswer" }
        case "turn_failed":
          return { kind: "failed", turnId: phase.turnId, reason: "runtime" }
        case "turn_abandoned":
          return { kind: "abandoned", turnId: phase.turnId }
        case "turn_duplicate":
          return {
            kind: "reconnecting",
            turnId: phase.turnId,
            startedAt: phase.startedAt,
            attempt: 1,
          }
        case "stream_end":
          return {
            kind: "answered",
            turnId: phase.turnId,
            answeredWhileAway: false,
            newSince: null,
          }
        case "stream_error":
          return {
            kind: "reconnecting",
            turnId: phase.turnId,
            startedAt: phase.startedAt,
            attempt: event.attempt,
          }
        case "status":
          return statusWhileWaiting(phase, event, 1)
        default:
          return phase
      }
    case "reconnecting":
      return event.type === "status" ? statusWhileWaiting(phase, event, phase.attempt) : phase
    case "stillWorking":
      // The socket may come back after a foreground check moved us here (iOS resumes a suspended
      // request): its terminal event is as good as a status read.
      if (event.type === "turn_completed") {
        return event.answerRecorded
          ? {
              kind: "answered",
              turnId: phase.turnId,
              answeredWhileAway: !event.screenFocused,
              newSince: null,
            }
          : { kind: "failed", turnId: phase.turnId, reason: "noAnswer" }
      }
      if (event.type === "turn_failed")
        return { kind: "failed", turnId: phase.turnId, reason: "runtime" }
      if (event.type === "turn_abandoned") return { kind: "abandoned", turnId: phase.turnId }
      if (event.type !== "status") return phase
      if (event.now > deadlineOf(phase.startedAt, phase.expiresAt) && event.status === "running")
        return { kind: "abandoned", turnId: phase.turnId }
      if (event.status === "unreachable") return { ...phase, polls: phase.polls + 1 }
      return statusWhileWaiting(phase, event, 2)
    case "answered":
    case "failed":
    case "abandoned":
      return phase
  }
}

function statusWhileWaiting(
  phase: Extract<TurnPhase, { kind: "live" | "reconnecting" | "stillWorking" }>,
  event: Extract<TurnEvent, { type: "status" }>,
  attemptFallback: 1 | 2
): TurnPhase {
  const startedAt = parseIso(event.startedAt) ?? phase.startedAt
  switch (event.status) {
    case "running":
      return {
        kind: "stillWorking",
        turnId: phase.turnId,
        startedAt,
        expiresAt: parseIso(event.expiresAt),
        polls: phase.kind === "stillWorking" ? phase.polls + 1 : 0,
      }
    case "completed":
      return event.answerRecorded
        ? {
            kind: "answered",
            turnId: phase.turnId,
            answeredWhileAway: true,
            newSince: newAnswerMarker(event.startedAt ?? new Date(startedAt).toISOString()),
          }
        : { kind: "failed", turnId: phase.turnId, reason: "noAnswer" }
    case "failed":
      return { kind: "failed", turnId: phase.turnId, reason: "runtime" }
    case "abandoned":
      return { kind: "abandoned", turnId: phase.turnId }
    case "unregistered":
    case "unreachable": {
      const progress = event.progress ?? emptyAttemptProgress()
      const attempt = event.attempt ?? attemptFallback
      const decision = decideAfterStreamLoss({ attempt, progress, serverStatus: event.status })
      if (decision.action === "retry")
        return { kind: "live", turnId: phase.turnId, startedAt: phase.startedAt }
      return {
        kind: "failed",
        turnId: phase.turnId,
        reason: failureAfterInterrupt(event.status, progress),
      }
    }
  }
}

/**
 * The turn's phase copy ("still working", "couldn't finish", "thinking") lives in the LAST assistant
 * bubble. After a cold restart or a reopened drawer the thread may end with the user's own question
 * (the answer is not in Memory yet) or be empty — then a synthetic empty assistant bubble carries it,
 * so a pending turn is never invisible. Pure: returns the same array when nothing is needed.
 */
export function threadWithPhaseBubble<M extends { role: string }>(
  messages: M[],
  showPhase: boolean,
  makeEmpty: () => M
): M[] {
  if (!showPhase) return messages
  const last = messages[messages.length - 1]
  if (last && last.role === "assistant") return messages
  return [...messages, makeEmpty()]
}

export function encodePendingTurn(record: PendingTurnRecord): string {
  return JSON.stringify({ ...record, v: 1, promptPreview: record.promptPreview.slice(0, 120) })
}

/** null for anything that is not a well-formed v1 record — a corrupt or foreign value is never trusted. */
export function decodePendingTurn(raw: string | null): PendingTurnRecord | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    if (!parsed || typeof parsed !== "object" || parsed.v !== 1) return null
    const s = (k: string) => (typeof parsed[k] === "string" ? (parsed[k] as string) : null)
    const turnId = s("turnId")
    const conversationId = s("conversationId")
    const runtimeSessionId = s("runtimeSessionId")
    const startedAt = s("startedAt")
    if (
      !turnId ||
      !conversationId ||
      !runtimeSessionId ||
      !startedAt ||
      !Number.isFinite(Date.parse(startedAt))
    )
      return null
    const expiresAt = parsed.expiresAt === null ? null : s("expiresAt")
    if (expiresAt !== null && !Number.isFinite(Date.parse(expiresAt))) return null
    return {
      v: 1,
      turnId,
      conversationId,
      runtimeSessionId,
      startedAt,
      expiresAt,
      promptPreview: (s("promptPreview") ?? "").slice(0, 120),
    }
  } catch {
    return null
  }
}

/** A record is worth one status read only while it is young enough that history-on-mount would not already have shown its outcome. */
export function isPendingRecordFresh(record: PendingTurnRecord, now: number): boolean {
  return now - Date.parse(record.startedAt) <= RECORD_MAX_AGE_MS
}

// ---------------------------------------------------------------------------------------------------
// F12 (2026-10-19) — the thread is addressed by TURN, never by position.
//
// The history fetch on mount is asynchronous. A user who sends before it resolves has an optimistic
// pair (their bubble + an empty assistant placeholder) on screen; replacing the whole thread with the
// restored history discarded that pair, and every stream updater that wrote into `messages[length - 1]`
// then wrote into the last OLD assistant message (text concatenated onto it, its tool spinner flipped,
// the user's own bubble gone). Two rules fix the class:
//   1. a restored history is MERGED under this session's live messages (mergeRestoredHistory), and
//   2. every in-flight write finds its bubble by the turn id it was minted with (updateAssistantByTurn),
//      and does nothing when that bubble is gone.
// ---------------------------------------------------------------------------------------------------

/** The slice of a client's ChatMessage these helpers need; web and mobile add their own image fields. */
export interface TurnMessage {
  role: "user" | "assistant"
  content: string
  toolActive?: boolean
  /** Set on messages created THIS session (the optimistic user bubble and its assistant placeholder); never on restored history. */
  turnId?: string
}

function sameMessage(restored: TurnMessage, live: TurnMessage): boolean {
  if (restored.role !== live.role) return false
  // An in-flight (empty) assistant placeholder is never "already recorded": it would hide the running turn.
  if (live.role === "assistant" && live.content.trim() === "") return false
  return restored.content.trim() === live.content.trim()
}

/**
 * Restored (server) history first, then this session's live messages that it does not already contain.
 * Live = messages carrying a `turnId`; anything without one in `live` is a previous restore and is
 * superseded by `restored`. A leading run of live messages that equals the tail of `restored` (the
 * server already recorded that turn) is dropped, so a pair is never shown twice. `keepTurnIds`, when
 * given, restricts the live messages to those turns (the away-answer refetch keeps only a turn that is
 * still in flight; every settled turn is already in the served history). Pure: never mutates.
 */
export function mergeRestoredHistory<M extends TurnMessage>(
  restored: M[],
  live: M[],
  options?: { keepTurnIds?: ReadonlySet<string> }
): M[] {
  const keep = options?.keepTurnIds
  const liveMsgs = live.filter(
    m => m.turnId !== undefined && (keep === undefined || keep.has(m.turnId))
  )
  if (liveMsgs.length === 0) return restored
  for (let k = Math.min(liveMsgs.length, restored.length); k >= 1; k--) {
    const tail = restored.slice(restored.length - k)
    if (tail.every((r, i) => sameMessage(r, liveMsgs[i]!)))
      return [...restored, ...liveMsgs.slice(k)]
  }
  return [...restored, ...liveMsgs]
}

/** Index of the assistant bubble that belongs to `turnId`, or -1. */
export function findAssistantIndex(messages: readonly TurnMessage[], turnId: string): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!
    if (m.role === "assistant" && m.turnId === turnId) return i
  }
  return -1
}

/** Apply `updater` to the turn's assistant bubble. Returns the SAME array when that bubble is gone (a no-op, never a write elsewhere). */
export function updateAssistantByTurn<M extends TurnMessage>(
  messages: M[],
  turnId: string,
  updater: (m: M) => M
): M[] {
  const at = findAssistantIndex(messages, turnId)
  if (at === -1) return messages
  const next = [...messages]
  next[at] = updater(messages[at]!)
  return next
}

/** Drop the turn's assistant bubble when it is still empty (the server never saw the turn). Same array when there is nothing to drop. */
export function dropEmptyAssistantByTurn<M extends TurnMessage>(
  messages: M[],
  turnId: string
): M[] {
  const at = findAssistantIndex(messages, turnId)
  if (at === -1 || messages[at]!.content) return messages
  return [...messages.slice(0, at), ...messages.slice(at + 1)]
}

/** The stream events that touch a bubble; the clients' richer event union is assignable to this. */
export type StreamBubbleEvent = { type: string; content?: unknown }

/** The ONE reduction of a stream event onto the thread: it lands in the turn's own assistant bubble, or nowhere. */
export function applyStreamEvent<M extends TurnMessage>(
  messages: M[],
  turnId: string,
  event: StreamBubbleEvent
): M[] {
  switch (event.type) {
    case "text": {
      const delta = event.content
      return updateAssistantByTurn(messages, turnId, m => ({
        ...m,
        content: m.content + (typeof delta === "string" ? delta : ""),
        toolActive: false,
      }))
    }
    case "tool_use_start":
      return updateAssistantByTurn(messages, turnId, m => ({ ...m, toolActive: true }))
    case "tool_result":
      return updateAssistantByTurn(messages, turnId, m => ({ ...m, toolActive: false }))
    default:
      return messages
  }
}
